import { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { formatMinor } from '@eprintsmart/shared';
import { LlmReferralsCard } from '../components/LlmReferralsCard';

interface ReportsData {
  windowDays: number;
  headline: {
    orders: number;
    grossMinor: number;
    netOfTaxMinor: number;
    averageOrderMinor: number;
    openOrders: number;
    overdueOrders: number;
    awaitingArtwork: number;
    awaitingAssignment: number;
  };
  revenue: {
    key: string;
    orders: number;
    /** Already net of refunds. */
    grossMinor: number;
    refundedMinor: number;
    /** What Razorpay kept, GST on the fee included. */
    gatewayFeeMinor: number;
    netOfTaxMinor: number;
    marginMinor: number | null;
    marginPercent: number | null;
  }[];
  categoryMix: { categoryId: string; categoryName: string; qty: number; netOfTaxMinor: number; sharePercent: number }[];
  artworkIssues: { code: string; fails: number; warns: number; total: number; sharePercent: number }[];
}

const fns = () => getFunctions(undefined, 'asia-south1');

export function Reports() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ReportsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recomputing, setRecomputing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    httpsCallable<{ days: number; granularity: string }, ReportsData>(fns(), 'getReports')({
      days,
      granularity: days > 90 ? 'month' : 'day',
    })
      .then(({ data: result }) => { if (!cancelled) setData(result); })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load reports.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [days]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await httpsCallable(fns(), 'recomputeVendorScorecards')({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not recompute scorecards.');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>Reports</Typography>
        <Button size="small" onClick={recompute} disabled={recomputing}>
          {recomputing ? 'Recomputing…' : 'Recompute vendor scorecards'}
        </Button>
        <TextField size="small" select value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ width: 150 }}>
          <MenuItem value={7}>Last 7 days</MenuItem>
          <MenuItem value={30}>Last 30 days</MenuItem>
          <MenuItem value={90}>Last 90 days</MenuItem>
          <MenuItem value={365}>Last year</MenuItem>
        </TextField>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 }}>
            <Stat label="Revenue" value={formatMinor(data.headline.grossMinor)} note="inclusive of GST" />
            <Stat label="Net of tax" value={formatMinor(data.headline.netOfTaxMinor)} note="what is actually ours" />
            <Stat label="Orders" value={String(data.headline.orders)} note={`avg ${formatMinor(data.headline.averageOrderMinor)}`} />
            <Stat label="Open orders" value={String(data.headline.openOrders)} note={`${data.headline.overdueOrders} overdue`} tone={data.headline.overdueOrders > 0 ? 'warning' : undefined} />
          </Box>

          {(data.headline.awaitingArtwork > 0 || data.headline.awaitingAssignment > 0) && (
            <Alert severity="info">
              {data.headline.awaitingArtwork} order(s) waiting on customer artwork ·{' '}
              {data.headline.awaitingAssignment} ready to assign to a vendor.
            </Alert>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Revenue</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Margin is calculated on revenue net of GST, since the tax was never ours, and now also
              net of what Razorpay keeps — around 2.54% all-in, which used to be reported as ours.
              Gross is after refunds. Margin is blank for periods where a vendor cost is still unknown.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Refunded</TableCell>
                  <TableCell align="right">Gateway fee</TableCell>
                  <TableCell align="right">Net of tax</TableCell>
                  <TableCell align="right">Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.revenue.slice(-20).reverse().map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.key}</TableCell>
                    <TableCell align="right">{row.orders}</TableCell>
                    <TableCell align="right">{formatMinor(row.grossMinor)}</TableCell>
                    <TableCell align="right" sx={{ color: row.refundedMinor > 0 ? 'warning.main' : 'text.secondary' }}>
                      {row.refundedMinor > 0 ? `−${formatMinor(row.refundedMinor)}` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      −{formatMinor(row.gatewayFeeMinor)}
                    </TableCell>
                    <TableCell align="right">{formatMinor(row.netOfTaxMinor)}</TableCell>
                    <TableCell align="right">
                      {row.marginPercent != null ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={row.marginPercent < 20 ? 'warning' : 'success'}
                          label={`${row.marginPercent}%`}
                        />
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {data.revenue.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No orders in this window.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Paper sx={{ p: 2, flex: 1, width: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>What sells</Typography>
              <Table size="small">
                <TableBody>
                  {data.categoryMix.map((row) => (
                    <TableRow key={row.categoryId}>
                      <TableCell>{row.categoryName}</TableCell>
                      <TableCell align="right">{row.qty}</TableCell>
                      <TableCell align="right">{formatMinor(row.netOfTaxMinor)}</TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Box sx={{ width: `${Math.max(row.sharePercent, 2)}%`, maxWidth: 40, height: 6, bgcolor: 'secondary.main', borderRadius: 3 }} />
                          <span>{row.sharePercent}%</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.categoryMix.length === 0 && (
                    <TableRow><TableCell align="center" sx={{ py: 3, color: 'text.secondary' }}>Nothing sold yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 2, flex: 1, width: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>Artwork problems</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Which checks trip most. A check that fails constantly is usually a product page that
                explains itself badly, not a run of careless customers.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Check</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Warned</TableCell>
                    <TableCell align="right">Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.artworkIssues.map((row) => (
                    <TableRow key={row.code}>
                      <TableCell>{row.code.replace(/_/g, ' ').toLowerCase()}</TableCell>
                      <TableCell align="right">{row.fails || '—'}</TableCell>
                      <TableCell align="right">{row.warns || '—'}</TableCell>
                      <TableCell align="right">{row.sharePercent}%</TableCell>
                    </TableRow>
                  ))}
                  {data.artworkIssues.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No artwork problems in this window.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        </>
      )}

      {/* Outside the block above on purpose: it reads Firestore directly rather
          than through getReports, so a slow or failing orders report must not
          take the referral numbers down with it. */}
      <LlmReferralsCard days={days} />
    </Stack>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'warning' }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="h2"
        sx={{ fontSize: '1.5rem', mt: 0.5, color: tone === 'warning' ? 'warning.main' : 'text.primary' }}
      >
        {value}
      </Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );
}
